/**
 * Brand Theme Provider
 *
 * Injects CSS custom properties for brand theming.
 * Updates document title and favicon based on brand configuration.
 *
 * This component should wrap the app at the provider level to ensure
 * CSS variables are available throughout the application.
 */

import { useEffect, type ReactNode } from 'react';
import { useBrand } from './BrandContext';

interface BrandThemeProviderProps {
  children: ReactNode;
}

type RgbValues = { r: number; g: number; b: number };

function hexToRgbValues(hex: string): RgbValues | null {
  const normalized = hex.trim().replace(/^#/, '');
  const isValidHex = /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(normalized);
  if (!isValidHex) {
    return null;
  }

  const fullHex =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => char + char)
          .join('')
      : normalized;

  return {
    r: Number.parseInt(fullHex.slice(0, 2), 16),
    g: Number.parseInt(fullHex.slice(2, 4), 16),
    b: Number.parseInt(fullHex.slice(4, 6), 16),
  };
}

function clampColorChannel(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function rgbToHex(rgb: RgbValues): string {
  const toHex = (value: number) => clampColorChannel(value).toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

function darkenHex(hex: string, amount: number): string | null {
  const rgb = hexToRgbValues(hex);
  if (!rgb) {
    return null;
  }

  const factor = 1 - Math.max(0, Math.min(1, amount));
  return rgbToHex({
    r: rgb.r * factor,
    g: rgb.g * factor,
    b: rgb.b * factor,
  });
}

function rgbaString(rgb: RgbValues, alpha: number): string {
  const clampedAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clampedAlpha})`;
}

function upsertFaviconLink(rel: string, sizes: string | null, href: string): void {
  const sizeSelector = sizes ? `[sizes='${sizes}']` : ':not([sizes])';
  const selector = `link[rel='${rel}']${sizeSelector}`;
  let link = document.querySelector(selector) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    if (sizes) {
      link.setAttribute('sizes', sizes);
    }
    document.head.appendChild(link);
  }

  link.href = href;
}

/**
 * Injects brand theme colors as CSS custom properties
 * and updates document metadata (title, favicon)
 */
export function BrandThemeProvider({ children }: BrandThemeProviderProps) {
  const brand = useBrand();

  useEffect(() => {
    // Guard: Skip theming if brand is not yet loaded
    // This allows the app to render while brand data is being fetched
    if (!brand) {
      return;
    }

    const root = document.documentElement;
    const themeMode = brand.theme.themeMode === 'light' ? 'light' : 'dark';

    root.setAttribute('data-theme', themeMode);

    // Inject brand colors as CSS custom properties
    root.style.setProperty('--brand-primary', brand.theme.primaryColor);
    root.style.setProperty(
      '--brand-primary-hover',
      brand.theme.primaryHoverColor || darkenHex(brand.theme.primaryColor, 0.12) || brand.theme.primaryColor
    );
    root.style.setProperty(
      '--brand-primary-active',
      brand.theme.primaryActiveColor || darkenHex(brand.theme.primaryColor, 0.25) || brand.theme.primaryColor
    );
    root.style.setProperty('--brand-secondary', brand.theme.secondaryColor);
    root.style.setProperty('--brand-background', brand.theme.backgroundColor);
    root.style.setProperty('--brand-text', brand.theme.textColor);

    // Override theme-mode base variables with brand-specific values
    root.style.setProperty('--verify-bg-dark', brand.theme.backgroundColor);
    root.style.setProperty('--verify-screen-bg', brand.theme.backgroundColor);
    if (brand.theme.surfaceSecondary) {
      root.style.setProperty('--verify-bg-secondary', brand.theme.surfaceSecondary);
    }

    const primaryRgb = hexToRgbValues(brand.theme.primaryColor);
    if (primaryRgb) {
      root.style.setProperty('--brand-primary-light', rgbaString(primaryRgb, 0.2));
      root.style.setProperty('--brand-primary-light-hover', rgbaString(primaryRgb, 0.3));
      root.style.setProperty('--brand-primary-medium', rgbaString(primaryRgb, 0.5));
      root.style.setProperty('--brand-primary-focus', rgbaString(primaryRgb, 0.2));
    }

    // Update document title with brand product name
    document.title = `${brand.productName} Verification`;

    // Update favicons if brand has custom ones
    if (brand.theme.faviconUrl) {
      // Detect if this is a pav- prefixed favicon (PrivateAV pattern)
      // e.g., /favicon/pav-32x32.png -> prefix is "pav-"
      const faviconMatch = brand.theme.faviconUrl.match(/\/favicon\/([a-z]+-)\d+x\d+\.png$/i);
      const faviconPrefix = faviconMatch ? faviconMatch[1] : null;

      // Update all favicon sizes if we have a prefix pattern
      if (faviconPrefix) {
        const sizes = ['16x16', '32x32', '192x192'];
        sizes.forEach(size => {
          const rel = size === '192x192' ? 'apple-touch-icon' : 'icon';
          upsertFaviconLink(rel, size, `/favicon/${faviconPrefix}${size}.png`);
        });
      } else {
        // Fallback: just update the primary favicon link
        upsertFaviconLink('icon', null, brand.theme.faviconUrl);
      }
    }
  }, [brand]);

  return <>{children}</>;
}
